import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    DKLabel,
    DKButton,
    DKIcon,
    DKIcons,
    DKListPicker2,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE,
    showAlert,
    DKContactIcon,
    DKTooltipWrapper
} from "deskera-ui-library";
import CreateRole from "./CreateRole";
import UserPermissionService from "./UserPermissionService";
import { convertCamelCaseToWord } from "../../managers/common/CommonUtil";
import { APP_NAME, CONSTANTS } from "../../constants/Constant";
import { getEllipsisText } from "../../utility/Utility";
import { TENANT, TenantService } from "../../managers/TenantManagerService";

function UserPermissionRow(props) {
    const { t } = useTranslation();

    const [showRolePicker, setShowRolePicker] = useState(false);
    const [isShowCreateRole, setIsShowCreateRole] = useState(false);

    useEffect(() => {

    }, []);

    const getUserFullName = (user) => {
        return user.status === "JOINED" ? `${user.firstName} ${user.lastName}` : user.email;
    }

    const getRoleOptions = (user) => {
        let options = [];
        if (Array.isArray(props.roles)) {
            switch (user.originalRole?.name) {
                case CONSTANTS.ADMIN:
                    options = props.roles.filter((role) => {
                        return (role.isDefault && role.name === "Admin") || (role.isSystemRole && role.key !== "no_access");
                    });
                    break;
                case CONSTANTS.MANAGER:
                    options = props.roles.filter((role) => {
                        return (!role.isDefault && !role.isSystemRole) || (role.isDefault && role.name === "Manager");
                    });
                    break;
                case CONSTANTS.HIRINGADMIN:
                    options = props.roles.filter((role) => {
                        return (!role.isDefault && !role.isSystemRole) || (role.isDefault && role.name === "Hiring Admin");
                    });
                    break;
                case CONSTANTS.USER:
                    options = props.roles.filter((role) => {
                        return (!role.isDefault && !role.isSystemRole) || (role.isSystemRole && role.key === "no_access") || (role.isDefault && role.name === "User");
                    });
                    break;
                default:
                    options = props.roles.filter((role) => {
                        return !role.isDefault && !role.isSystemRole;
                    });
                    break;
            }
        }

        return options;
    }

    const onRoleChange = (role, user) => {
        const payload = {
            userId: props.user.userId,
            roleId: role.id,
            update: !!user.role
        };

        showLoader("Assigning role to user... Please wait");
        UserPermissionService.assignRole(payload).then(
            (response) => {
                if (user.iamUserId === TenantService.get(TENANT.IAM_USER_ID)) {
                    window.location.reload();
                    return;
                }

                props.fetchUserRoles();
                removeLoader();
                setShowRolePicker(false);
                showToast("Assigned role successfully.", TOAST_TYPE.SUCCESS);
            }, (error) => {
                removeLoader();
                setShowRolePicker(false);
                if (error?.message) {
                    showToast(convertCamelCaseToWord(error.message), TOAST_TYPE.FAILURE);
                } else {
                    showToast("Error while assigning role, Please try after sometime.", TOAST_TYPE.FAILURE);
                }
            }
        );
    }

    const onDeleteRole = (role) => {
        showLoader("Deleting role... Please wait");
        UserPermissionService.deleteRole(role.id).then(
            (response) => {
                props.fetchRoles();
                props.fetchUserRoles();
                setShowRolePicker(false);
                removeLoader();
                showToast("Deleted role successfully.", TOAST_TYPE.SUCCESS);
            }, (error) => {
                setShowRolePicker(false);
                removeLoader();
                showToast("Error while deleting role, Please try after sometime.", TOAST_TYPE.FAILURE);
            }
        );
    }

    const onRoleSelect = (role, user) => {
        if (role.id === user.role?.id) {
            setShowRolePicker(false);
            return;
        }

        let message = `Are you sure want to change the user role to<br/><b>${role.name}</b>?`;
        if (user.iamUserId === TenantService.get(TENANT.IAM_USER_ID)) {
            message = `Are you sure want to change your current role to<br/><b>${role.name}</b>? Updating your role may impact your ability to modify roles. The application will reload to apply the updated role permissions.`;
        }

        showAlert('Confirm', message, [
            {
                title: 'Yes',
                className: 'bg-app text-white mr-r',
                onClick: () => {
                    onRoleChange(role, user);
                },
            },
            {
                title: 'No',
                className: 'bg-gray1 border-m',
            },
        ]);
    }

    const renderRolePicker = (user) => {
        return (
            <DKListPicker2
                className="position-absolute border-m shadow-s border-radius-s z-index-3"
                style={{ right: 0, top: 0, width: 250 }}
                data={getRoleOptions(user)}
                displayKey={"name"}
                searchableKey={"name"}
                allowSearch={true}
                canEdit={true}
                canDelete={true}
                onSelect={(index, role) => {
                    onRoleSelect(role, user);
                }}
                renderer={(index, obj) => {
                    return (
						<DKTooltipWrapper
							content={obj["name"]}
							tooltipClassName="warning-tooltip-prop warning-tooltip-prop "
						>
							<DKLabel
								style={{ maxWidth: 165 }}
								className="ellipsis"
								text={obj["name"]}
							/>
						</DKTooltipWrapper>
					);
                }}
                onEdit={(index, role) => {
                    UserPermissionService.ROLE_DATA = role;
                    setIsShowCreateRole(true);
                }}
                onDelete={(index, role) => {
                    if (role.isDefault || role.isSystemRole) {
                        showAlert("Operation not allowed!", "You can not delete the default role.");
                        return;
                    } else {
                        onDeleteRole(role);
                    }
                }}
                button={{
                    title: "+ New Role",
                    className: "bg-button text-white",
                    onClick: () => {
                        setIsShowCreateRole(true);
                    }
                }}
                onClose={() => {
                    setShowRolePicker(false);
                }}
            />
        );
    }

    const getRoleName = (user, isDisabled) => {
        if(props?.isOwner) {
            return 'Owner';
        } else {
            let roleName = isDisabled ? '-' : 'Select Role';
            if (user?.role?.id && Array.isArray(props.roles)) {
                const role = props.roles.find((role => role.id === user.role.id));
                if (role) {
                    roleName = role.name;
                }
            } else if (user?.originalRole?.name) {
                switch(user?.originalRole?.name) {
                    case CONSTANTS.MANAGER:
                        roleName = 'Manager';
                        break;
                    case CONSTANTS.ADMIN:
                        roleName = 'Admin';
                        break;
                    case CONSTANTS.HIRINGADMIN:
                        roleName = 'Hiring Admin';
                        break;
                    case CONSTANTS.OWNER:
                        roleName = 'Owner';
                        break;
                    default: 
                        roleName = isDisabled ? '-' : 'Select Role';
                        break;
                }
            }

            return roleName;
        }
    }

    const resendInvite = (user) => {
        const payload = {
            appName: APP_NAME,
            email: user.email,
            role: user.role
        };

        showLoader("Inviting user...");
		UserPermissionService.inviteUser(payload).then(
			(response) => {
				removeLoader();
                props.fetchInvites();
				showAlert(
					`Invitation sent!`,
					`Invitation has been sent to <strong>${user.email}</strong>. Please ask user to check the mail inbox for invitation email and accept it.`
				);
			},
			(error) => {
				removeLoader();
				if (error?.code === 403) {
					showAlert(
						`Not Allowed!`,
						`Looks like you are not authorize to invite user please contact your organization owner.`
					);
				}
			}
		);
    }

    const deleteInvite = (user) => {
        UserPermissionService.cancelInvite(user.id).then(
            (response) => {
                props.fetchInvites();
            }, (error) => {
                showAlert(
                    "Error occurred!",
                    "Error occurred while deleting invite, Please try again later."
                );
            }
        );
    }

    const renderUserActions = (user) => {
        return (
			<Fragment>
				<div
					className="position-relative row width-auto justify-content-end "
					style={{ maxWidth: "40%" }}
				>
					<div className="ellipsis">
						{user.status === "JOINED" && (
                            <DKTooltipWrapper
                                content={getRoleName(user, props?.isDisabled)}
                                tooltipClassName="warning-tooltip-prop warning-tooltip-prop "
                            >
                                <DKButton
                                    title={getEllipsisText(getRoleName(user, props?.isDisabled), 25)}
                                    className="text-gray user-role-selector-button"
                                    icon={!props?.isDisabled && DKIcons.ic_arrow_down2}
                                    isReverse={true}
                                    disabled={props?.isDisabled}
                                    onClick={() => {
                                        setShowRolePicker(true);
                                    }}
                                />
                            </DKTooltipWrapper>	
						)}
						{user.status === "SENT" && (
							<div className="row">
                                <DKIcon
                                    className="ic-s p-h-s p-v-xs mr-r"
                                    src={DKIcons.ic_repeat}
                                    style={{ opacity: 0.75 }}
                                    onClick={() => {
                                        resendInvite(user);
                                    }}
                                />
                                <DKIcon
                                    className="ic-s p-h-s p-v-xs"
                                    src={DKIcons.ic_delete}
                                    style={{ opacity: 0.75 }}
                                    onClick={() => {
                                        showAlert(
                                            "Delete Invite?",
                                            "Deleting this invite will delete it permanently you will not be able to restore it.",
                                            [
                                                {
                                                    title: "Cancel",
                                                    className: "bg-gray1 border-m"
                                                },
                                                {
                                                    title: "Delete",
                                                    className: "bg-red text-white ml-r",
                                                    onClick: () => {
                                                        deleteInvite(user);
                                                    }
                                                },
                                            ]
                                        );
                                    }}
                                />
							</div>
						)}
					</div>
					{showRolePicker && renderRolePicker(user)}
				</div>
			</Fragment>
		);
    }

    const renderUserRow = (user) => {
        return (
            <div className="row" style={{ maxWidth: "60%" }}>
                <DKContactIcon title={getUserFullName(user)} className={"flex-shrink-0 border-m display-only-web"} />
                <div className="column ml-m width-auto margin-empty-mobile max-width-100">
                    <DKLabel text={getUserFullName(user)} className="fw-m mb-xs" />
                    <DKLabel
                        text={user.status === "JOINED" ? user.email : "Pending"}
                        className={`parent-width ${user.status === "JOINED" ? "text-gray" : "text-red"}`}
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    />
                </div>
                {isShowCreateRole &&
                    <CreateRole
                        modules={props.modules}
                        onRoleCreate={(role) => {
                            if (user.originalRole?.name !== "ADMIN") {
                                onRoleChange(role, user);
                            }
                        }}
                        onClose={(refresh) => {
                            setIsShowCreateRole(false);
                            if (refresh) {
                                props.fetchRoles();
                                props.fetchUserRoles();
                            }
                        }}
                    />
                }
            </div>
        );
    }

    return (
        <div className="row justify-content-between p-h-r p-v-m user-row-wrap">
            {renderUserRow(props.user)}
            {renderUserActions(props.user)}
        </div>
    );
}

export default UserPermissionRow;
